import type { Branded } from "../../shared/kernel/identifiers.js";

export const RoleCode = {
  Developer: "DEVELOPER",
  Admin: "ADMIN",
  Engineer: "ENGINEER",
  Technician: "TECHNICIAN",
  Inspector: "INSPECTOR",
  Documentation: "DOCUMENTATION",
  Manager: "MANAGER",
  Viewer: "VIEWER"
} as const;

// Manter seis perfis selecionáveis; MANAGER segue no enum legado, fora do catálogo UI.
export const OFFICIAL_ROLES = [
  { code: RoleCode.Developer, label: "Desenvolvedor" },
  { code: RoleCode.Admin, label: "Administrador" },
  { code: RoleCode.Engineer, label: "Engenheiro" },
  { code: RoleCode.Inspector, label: "Inspetor" },
  { code: RoleCode.Documentation, label: "Técnico de Documentação" },
  { code: RoleCode.Viewer, label: "Visualizador" }
] as const;

export type BuiltInRoleCode = (typeof RoleCode)[keyof typeof RoleCode];
export type CustomRoleCode = Branded<string, "CustomRoleCode">;
export type RoleCode = BuiltInRoleCode | CustomRoleCode;

export const PermissionCode = {
  ClientAccess: "client.access",
  ClientRead: "client.read",
  ClientCreate: "client.create",
  ClientUpdate: "client.update",
  ClientDelete: "client.delete",
  ClientManage: "client.manage",
  ProjectRead: "project.read",
  ProjectCreate: "project.create",
  ProjectUpdate: "project.update",
  ProjectDelete: "project.delete",
  ProjectManage: "project.manage",
  UnitAreaRead: "unit_area.read",
  UnitAreaCreate: "unit_area.create",
  UnitAreaUpdate: "unit_area.update",
  UnitAreaDelete: "unit_area.delete",
  UserRead: "user.read",
  UserCreate: "user.create",
  UserUpdate: "user.update",
  UserDelete: "user.delete",
  UserManage: "user.manage",
  IdentityRead: "identity.read",
  IdentityManage: "identity.manage",
  ProfessionalRead: "professional.read",
  ProfessionalCreate: "professional.create",
  ProfessionalUpdate: "professional.update",
  ProfessionalDelete: "professional.delete",
  ProfessionalManage: "professional.manage",
  SignatoryRead: "signatory.read",
  SignatoryCreate: "signatory.create",
  SignatoryUpdate: "signatory.update",
  SignatoryDelete: "signatory.delete",
  SignatoryManage: "signatory.manage",
  EquipmentRead: "equipment.read",
  EquipmentCreate: "equipment.create",
  EquipmentUpdate: "equipment.update",
  EquipmentDelete: "equipment.delete",
  EquipmentInactivate: "equipment.inactivate",
  EquipmentReactivate: "equipment.reactivate",
  /** Bloco 04: importação em lote de Sistemas/Tubulações — ato mais poderoso que criar um equipamento isolado, por isso tem código próprio. */
  EquipmentImport: "equipment.import",
  InstrumentRead: "instrument.read",
  InstrumentCreate: "instrument.create",
  InstrumentUpdate: "instrument.update",
  InstrumentDelete: "instrument.delete",
  InstrumentInactivate: "instrument.inactivate",
  InstrumentReactivate: "instrument.reactivate",
  SafetyDeviceRead: "safetyDevice.read",
  SafetyDeviceCreate: "safetyDevice.create",
  SafetyDeviceUpdate: "safetyDevice.update",
  SafetyDeviceDelete: "safetyDevice.delete",
  SafetyDeviceInactivate: "safetyDevice.inactivate",
  SafetyDeviceReactivate: "safetyDevice.reactivate",
  TechnicalAssetDocumentsManage: "technicalAsset.documents.manage",
  InspectionRead: "inspection.read",
  InspectionReadAssigned: "inspection.read_assigned",
  InspectionReadAll: "inspection.read_all",
  InspectionCreate: "inspection.create",
  InspectionUpdate: "inspection.update",
  /**
   * CONTROLE DE CAMPO — avanço das inspeções por Linha. Separado das
   * permissões de Inspeção porque o recorte é outro: o Inspetor aponta
   * avanço só nos Sistemas a que foi vinculado (`view_own`/`update_own`),
   * enquanto Técnico/Engenheiro acompanham todos e gerenciam os vínculos.
   */
  FieldControlViewOwn: "field_control.view_own",
  FieldControlUpdateOwn: "field_control.update_own",
  FieldControlViewAll: "field_control.view_all",
  FieldControlManageAssignments: "field_control.manage_assignments",
  ReportRead: "report.read",
  ReportReadAssigned: "report.read_assigned",
  ReportReadAll: "report.read_all",
  ReportCreate: "report.create",
  ReportUpdate: "report.update",
  ReportDelete: "report.delete",
  /**
   * Designar o Inspetor Responsável de um RI diferente de si mesmo —
   * mesma regra do `MeReportAssignInspector` (ver me-inspector-subos.ts).
   * Quem não a possui tem o campo resolvido automaticamente pelo
   * Professional da própria conta.
   */
  ReportAssignInspector: "report.assign_inspector",
  ReportReview: "report.review",
  ReportApprove: "report.approve",
  ReportSign: "report.sign",
  AdministrationRead: "administration.read",
  AdministrationManage: "administration.manage",
  ManagementAccess: "management.access",
  EngineeringAccess: "engineering.access",
  CalculationAccess: "calculation.access",
  AvrAccess: "avr.access",
  /** M16A: Permite acesso ao Painel de Avanços e Indicadores (/dashboard/indicators). */
  DashboardIndicatorsView: "dashboard.indicators.view",
  SettingsAccess: "settings.access",
  // CONFIGURAÇÕES — uma permissão por área sensível: ler/alterar numeração não
  // dá acesso a segredo de ICP, e ver diagnóstico não dá direito de executá-lo.
  SettingsRead: "settings.read",
  SettingsUpdate: "settings.update",
  SettingsWorkflowUpdate: "settings.workflow.update",
  SettingsImportUpdate: "settings.import.update",
  SettingsSignatureRead: "settings.signature.read",
  SettingsSignatureUpdate: "settings.signature.update",
  SettingsIntegrationRead: "settings.integration.read",
  SettingsIntegrationUpdate: "settings.integration.update",
  SettingsSecurityRead: "settings.security.read",
  SettingsSecurityUpdate: "settings.security.update",
  SettingsDiagnosticsRead: "settings.diagnostics.read",
  SettingsDiagnosticsExecute: "settings.diagnostics.execute",
  SettingsAuditRead: "settings.audit.read",
  /** ZONA DE RISCO — Reset controlado de todos os dados operacionais da base com reautenticação de senha. */
  SystemDatabaseReset: "system.database.reset",
  AiStudioAccess: "studio.access",
  InspectorSearch: "inspector.search",
  SubOsSearch: "sub_os.search",
  SubWorkOrderRead: "sub_work_order.read",
  SubWorkOrderCreate: "sub_work_order.create",
  SubWorkOrderUpdate: "sub_work_order.update",
  SubWorkOrderDelete: "sub_work_order.delete",
  RbacManage: "rbac.manage",
  DocumentRead: "document.read",
  DocumentWrite: "document.write",
  DocumentSign: "document.sign",
  DocumentIssue: "document.issue",
  AuditRead: "audit.read",
  CatalogRead: "catalog.read",
  CatalogCreate: "catalog.create",
  CatalogUpdate: "catalog.update",
  CatalogInactivate: "catalog.inactivate",
  StudioCreate: "studio.create",
  StudioUpdate: "studio.update",
  StudioTest: "studio.test",
  StudioPublish: "studio.publish",
  StudioInactivate: "studio.inactivate",
  StudioArchive: "studio.archive",
  // STUDIO INSPECT IA — aparência. "studio.publish" (acima) é reutilizado para
  // publicar a identidade visual; editar exige a permissão de cada área.
  StudioRead: "studio.read",
  StudioIdentityRead: "studio.identity.read",
  StudioIdentityUpdate: "studio.identity.update",
  StudioThemeRead: "studio.theme.read",
  StudioThemeUpdate: "studio.theme.update",
  StudioLoginUpdate: "studio.login.update",
  StudioDocumentsRead: "studio.documents.read",
  StudioDocumentsUpdate: "studio.documents.update",
  StudioTemplatesRead: "studio.templates.read",
  StudioTemplatesUpdate: "studio.templates.update",
  StudioAuditRead: "studio.audit.read",
  // PAINEL DE INDICADORES — link compartilhado (Correção 9). Criar, ver os
  // próprios links e revogá-los; "manage" enxerga e revoga os de todos do
  // Cliente. O Painel interno continua lido pelas permissões de cada
  // cadastro — não existe um "dashboard.read" que valha mais que elas.
  DashboardShareCreate: "dashboard.share.create",
  DashboardShareRead: "dashboard.share.read",
  DashboardShareRevoke: "dashboard.share.revoke",
  DashboardShareManage: "dashboard.share.manage",
  /**
   * @deprecated LEGADO do Córtex (descontinuado em 2026-09-27). Não é mais concedida
   * por perfil nem confere acesso a nada; um usuário que a tenha como permissão
   * individual recebe as equivalentes do INSPECT IA REVIEW (effective-permissions.ts).
   */
  CortexUse: "cortex.use",
  // INSPECT IA REVIEW — camada oficial de IA.
  AiReviewRead: "ai_review.read",
  AiReviewExecute: "ai_review.execute",
  AiReviewApplyLanguage: "ai_review.apply_language",
  AiReviewResolveFinding: "ai_review.resolve_finding",
  AiReviewHistory: "ai_review.history",
  AiAssistantUse: "ai_assistant.use",
  AiSettingsRead: "ai_settings.read",
  AiSettingsUpdate: "ai_settings.update",
  NdtRegistryRead: "ndt_registry.read",
  NdtRegistryCreate: "ndt_registry.create",
  NdtRegistryUpdate: "ndt_registry.update",
  NdtRegistryInactivate: "ndt_registry.inactivate",
  /** Bloco 14: physical deletion of an END registry record (only ever allowed when nothing references it). */
  NdtRegistryDelete: "ndt_registry.delete",
  ChecklistRead: "checklist.read",
  ChecklistUpdate: "checklist.update",
  RecommendationRead: "recommendation.read",
  RecommendationCreate: "recommendation.create",
  RecommendationUpdate: "recommendation.update",
  RiRead: "ri.read",
  RiCreate: "ri.create",
  RiUpdate: "ri.update",
  NdtRead: "ndt.read",
  NdtCreate: "ndt.create",
  NdtUpdate: "ndt.update",
  AvrRead: "avr.read",
  AvrCreate: "avr.create",
  AvrUpdate: "avr.update",
  /** AVR — Avaliação de Vida Remanescente. Approve/Sign são de PLH — ver PLH_SPECIFIC_PERMISSIONS. */
  AvrSelectSources: "avr.select_sources",
  AvrCalculate: "avr.calculate",
  AvrValidate: "avr.validate",
  AvrApprove: "avr.approve",
  AvrSign: "avr.sign",
  AvrGeneratePdf: "avr.generate_pdf",
  /** Workflow AVR ↔ PLH. Submit é do Engenheiro; review/correction/approve são de PLH — ver PLH_SPECIFIC_PERMISSIONS. */
  AvrSubmitForPlhReview: "avr.submit_for_plh_review",
  AvrPlhReview: "avr.plh_review",
  AvrRequestCorrection: "avr.request_correction",
  AvrApprovePlhReview: "avr.approve_plh_review",
  PlhReview: "plh.review",
  PlhApprove: "plh.approve",
  PlhReturn: "plh.return",
  SignatureSign: "signature.sign",
  RdoRead: "rdo.read",
  RdoCreate: "rdo.create",
  RdoUpdate: "rdo.update",
  RdoDelete: "rdo.delete",
  RdoConsolidate: "rdo.consolidate",
  DocumentRevise: "document.revise",
  DocumentCancel: "document.cancel",
  ProfessionalSubOsRead: "professionalSubOs.read",
  ProfessionalSubOsAssign: "professionalSubOs.assign",
  ProfessionalSubOsRemove: "professionalSubOs.remove",
  ProfessionalSubOsInactivate: "professionalSubOs.inactivate",
  EngineeringViewWorkspace: "engineering.view_workspace",
  EngineeringStartAnalysis: "engineering.start_analysis",
  EngineeringEditRecommendation: "engineering.edit_recommendation",
  EngineeringRequestCorrection: "engineering.request_correction",
  EngineeringDelegateToInspector: "engineering.delegate_to_inspector",
  EngineeringValidateCorrection: "engineering.validate_correction",
  /** @deprecated Superseded by EngineeringValidateMinuta (Bloco 6) — "Concluir Análise" was a redundant step between validating and approving the Minuta. Kept only so any already-persisted grant referencing this code does not become a dangling string. */
  EngineeringCompleteAnalysis: "engineering.complete_analysis",
  EngineeringValidateMinuta: "engineering.validate_minuta",
  EngineeringApproveMinuta: "engineering.approve_minuta",
  EngineeringSignMinuta: "engineering.sign_minuta",
  /**
   * Bloco 22: editar/remover a numeração ZR de uma Recommendation. É ação de
   * Engenharia sobre o número, NÃO assinatura PLH — não passa por
   * canActAsPlh(). Concedidas por padrão só ao ENGINEER; nem o ADMIN as recebe
   * pelo papel (ver ENGINEERING_SPECIFIC_PERMISSIONS).
   */
  EngineeringEditZrNumber: "engineering.edit_zr_number",
  EngineeringRemoveZrNumber: "engineering.remove_zr_number",
  MinutaRequestRevision: "minuta.request_revision",
  MinutaReturnForCorrection: "minuta.return_for_correction",
  /** Bloco 9: RI (Integrity Report) review actions, mirroring the Minuta review permissions above. */
  IntegrityReportView: "integrity_report.view",
  IntegrityReportEdit: "integrity_report.edit",
  IntegrityReportValidate: "integrity_report.validate",
  IntegrityReportGeneratePdf: "integrity_report.generate_pdf",
  IntegrityReportApprove: "integrity_report.approve",
  IntegrityReportSign: "integrity_report.sign",
  /** Bloco 11: Relatório de Medição de Espessura (ME). Approve/Sign are PLH-specific — see PLH_SPECIFIC_PERMISSIONS. */
  MeReportView: "me_report.view",
  MeReportCreate: "me_report.create",
  MeReportEdit: "me_report.edit",
  MeReportValidate: "me_report.validate",
  MeReportGeneratePdf: "me_report.generate_pdf",
  MeReportApprove: "me_report.approve",
  MeReportSign: "me_report.sign",
  /**
   * Escolher o Inspetor Responsável de um ME que não seja você mesmo.
   *
   * Quem NÃO a tem (o próprio Inspetor, o Técnico) recebe o campo resolvido
   * automaticamente pelo Professional da própria conta — é o comportamento
   * correto para quem executa o ensaio, e evita que alguém emita um relatório
   * em nome de outra pessoa. Administrador e Engenheiro a recebem.
   */
  MeReportAssignInspector: "me_report.assign_inspector",
  /** Relatório de Líquido Penetrante (LP). Approve/Sign são de PLH — ver PLH_SPECIFIC_PERMISSIONS. */
  LpReportView: "lp_report.view",
  LpReportCreate: "lp_report.create",
  LpReportEdit: "lp_report.edit",
  LpReportValidate: "lp_report.validate",
  LpReportGeneratePdf: "lp_report.generate_pdf",
  LpReportApprove: "lp_report.approve",
  LpReportSign: "lp_report.sign",
  /** Escolher os três signatários do LP (Inspetor, PLH, Fiscal) — decisão de Engenharia; ver ENGINEERING_SPECIFIC_PERMISSIONS. */
  LpReportManageSignatories: "lp_report.manage_signatories",
  /** Relatório de Partículas Magnéticas (PM). Approve/Sign são de PLH — ver PLH_SPECIFIC_PERMISSIONS. */
  PmReportView: "pm_report.view",
  PmReportCreate: "pm_report.create",
  PmReportEdit: "pm_report.edit",
  PmReportValidate: "pm_report.validate",
  PmReportGeneratePdf: "pm_report.generate_pdf",
  PmReportApprove: "pm_report.approve",
  PmReportSign: "pm_report.sign",
  PmReportManageSignatories: "pm_report.manage_signatories",
  /** Relatório de Ultrassom Convencional (USC). Approve/Sign são de PLH — ver PLH_SPECIFIC_PERMISSIONS. */
  UscReportView: "usc_report.view",
  UscReportCreate: "usc_report.create",
  UscReportEdit: "usc_report.edit",
  UscReportValidate: "usc_report.validate",
  UscReportGeneratePdf: "usc_report.generate_pdf",
  UscReportApprove: "usc_report.approve",
  UscReportSign: "usc_report.sign",
  UscReportManageSignatories: "usc_report.manage_signatories",
  // B-SCAN / C-SCAN — mesmo conjunto dos demais END por método. As ações do
  // fluxo do PLH (iniciar análise, correção, aprovar) seguem nas permissões
  // centrais de TechnicalReview*: o B-Scan não tem fluxo paralelo.
  BscanReportView: "bscan_report.view",
  BscanReportCreate: "bscan_report.create",
  BscanReportEdit: "bscan_report.edit",
  BscanReportValidate: "bscan_report.validate",
  BscanReportGeneratePdf: "bscan_report.generate_pdf",
  BscanReportApprove: "bscan_report.approve",
  BscanReportSign: "bscan_report.sign",
  BscanReportManageSignatories: "bscan_report.manage_signatories",
  /** Revisão e aprovação PLH — mecanismo central para LP/ME/PM/… Review/Correction/Approve/Sign são de PLH. */
  TechnicalReviewSubmitToPlh: "technical_review.submit_to_plh",
  TechnicalReviewViewQueue: "technical_review.view_queue",
  TechnicalReviewReview: "technical_review.review",
  TechnicalReviewRequestCorrection: "technical_review.request_correction",
  TechnicalReviewApprove: "technical_review.approve",
  TechnicalReviewSign: "technical_review.sign",
  /**
   * PAINEL DE ENGENHARIA (central de gestão técnica). Códigos no padrão do
   * catálogo (um ponto: `dominio.acao`): engineering.dashboard.view →
   * engineering.dashboard_view, e assim por diante. Approve / request
   * correction são de PLH — ver PLH_SPECIFIC_PERMISSIONS.
   */
  EngineeringDashboardView: "engineering.dashboard_view",
  EngineeringReviewView: "engineering.review_view",
  EngineeringReviewApprove: "engineering.review_approve",
  EngineeringReviewRequestCorrection: "engineering.review_request_correction",
  EngineeringMinuteCreate: "engineering.minute_create",
  EngineeringMinuteEdit: "engineering.minute_edit",
  EngineeringHoldView: "engineering.hold_view",
  EngineeringHoldComment: "engineering.hold_comment",
  /** HOLD de documentos em elaboração (Inspeção/Checklist/END) — ato de quem elabora. */
  ReportPutOnHold: "report.put_on_hold",
  ReportRemoveFromHold: "report.remove_from_hold",
  /** Ferramenta Central de Backup e Restauração (Administração). */
  BackupRead: "backup.read",
  BackupCreate: "backup.create",
  BackupRestore: "backup.restore",
  BackupDelete: "backup.delete",
  BackupManage: "backup.manage"
} as const;

export type BuiltInPermissionCode =
  (typeof PermissionCode)[keyof typeof PermissionCode];
export type CustomPermissionCode = Branded<string, "CustomPermissionCode">;
export type PermissionCode = BuiltInPermissionCode | CustomPermissionCode;

const ROLE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{2,63}$/;
const PERMISSION_CODE_PATTERN = /^[a-z][a-z0-9-]{1,63}\.[a-z][a-z0-9-]{1,63}$/;

export function roleCode(value: string): RoleCode {
  if (!ROLE_CODE_PATTERN.test(value)) {
    throw new TypeError("Role codes must be uppercase identifiers");
  }
  return value as RoleCode;
}

export function permissionCode(value: string): PermissionCode {
  if (!PERMISSION_CODE_PATTERN.test(value)) {
    throw new TypeError("Permission codes must use resource.action format");
  }
  return value as PermissionCode;
}
